import { Row, Col, Form, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCircleInfo, faCircleXmark, faPlay, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import XInput from '../../../component/form/XInput';
import XSelect from '../../../component/form/XSelect';
import { toaster } from '../../../utils/toast';
import useAxios from '../../../hooks/useAxios';
import { API_ENDPOINTS } from '../../../constants/api';
import LocationCard from './LocationCard';
import useCompany from './useCompany';
import useLocationData from '../../../hooks/useLocationData';
import XBackButton from '../../../component/button/XBackButton';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setUserData } from '../../../redux/modules/auth/userSlice';
import { RootState } from '../../../redux/rootReducer';
import useSidebar from '../../../hooks/useSidebar';
import { useState, useEffect, useRef } from 'react';
import XButton from '../../../component/button/XButton';
import PhoneNumberInput from '../../../component/phoneInput/phoneInput';
import { ROLE } from '../../../utils/enums';

const CAN_MANAGE_LOCATIONS = [ROLE.ORG, ROLE.ACCOUNT_ADMIN];
const MAX_LOGO_SIZE_BYTES = 1024 * 1024;

const EditCompany = () => {
  const { role_id } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { postData, fetchData, deleteData, isLoading } = useAxios()
  const { companyDetail } = useCompany();
  const { onShowSidebar } = useSidebar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isLogoRemoved, setIsLogoRemoved] = useState<boolean>(false);
  const { countries, states, fetchStates } = useLocationData();
  const canManageLocations = CAN_MANAGE_LOCATIONS.includes(Number(role_id));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: companyDetail?.email ?? '',
      firstName: companyDetail?.first_name ?? '',
      lastName: companyDetail?.last_name ?? '',
      organizationName: companyDetail?.business_name ?? '',
      logo: companyDetail?.filename,
      websiteURL: companyDetail?.website_url ?? '',
      phoneNumber: companyDetail?.phone_number ?? '',
      // Personal address — lives on the users table, editable by ANY role
      address: companyDetail?.address ?? '',
      zipcode: companyDetail?.zipcode ?? '',
      country_id: companyDetail?.country_id ?? '',
      state_id: companyDetail?.state_id ?? '',
      city: companyDetail?.city ?? '',
      question_id: companyDetail?.question_id ?? '',
      answer: companyDetail?.two_factor_answer ?? '',
      // Org business locations — only rendered/editable for Org/Account Admin
      locations: (companyDetail?.locations ?? [{
        display_name: '',
        country_id: '',
        state_id: '',
        city: '',
        address: '',
        zipcode: '',
        reply_to_email: '',
        from_name: '',
        is_active: true,
        location_timezone: 'UTC',
        rss_feeds: [],
      }]).map((loc: any, idx: number) => ({
        ...loc,
        display_name: loc.display_name ?? '',
        reply_to_email: loc.reply_to_email ?? '',
        from_name: loc.from_name ?? '',
        location_timezone: loc.location_timezone || 'UTC',
        _tempId: loc.id || loc.location_id || `loc-init-${idx}`,
        rss_feeds: loc.rss_feeds ?? (loc.rss_feed_url ? [{ feed_name: 'RSS Feed', feed_url: loc.rss_feed_url, max_items: loc.rss_max_items ?? '' }] : []),
      })),
    },
    validationSchema: Yup.object().shape({
    question_id: Yup.string().test('isRequired', 'Question is required when Answer is provided', function (value) {
      const answer = this.resolve(Yup.ref('answer'));
      return !answer || !!value;
    }),
    answer: Yup.string(),
    firstName: Yup.string().trim().required('First Name is required'),
    lastName: Yup.string().trim().required('Last Name is required'),
    address: Yup.string().trim().required('Address is required'),
    zipcode: Yup.string()
      .required('Zipcode is required')
      .matches(/^\d{5}$/, 'Zipcode must be a number of 5 digits'),
    country_id: Yup.string().required('Country is required'),
    state_id: Yup.string().required('State is required'),
    city: Yup.string().trim().required('City is required'),
    ...(+role_id === ROLE.ORG && {
      organizationName: Yup.string().trim().required('Organization Name is required'),
      logo: Yup.mixed().nullable(),
      websiteURL: Yup.string().trim().required('Website URL is required'),
    }),
    ...(canManageLocations && {
      locations: Yup.array().of(
        Yup.object().shape({
          display_name: Yup.string().trim().required('Location Name is required'),
          address: Yup.string().trim().required('Address is required').test(
            (value) => value.trim() !== ''
          ),
          zipcode: Yup.string()
            .required('Zipcode is required')
            .matches(/^\d{5}$/, 'Zipcode must be a number of 5 digits'),
          reply_to_email: Yup.string().trim().email('Enter a valid email address').required('Reply-to Email is required'),
          from_name: Yup.string().trim().required('From Email Name is required'),
          location_timezone: Yup.string().required('Timezone is required'),
          country_id: Yup.string().required('country is required'),
          state_id: Yup.string().required('state is required'),
          city: Yup.string().trim().required('city is required').test(
            (value) => value.trim() !== ''
          ),
          rss_feeds: Yup.array().of(Yup.object().shape({
            feed_name: Yup.string().trim().required('RSS Feed Name is required'),
            feed_url: Yup.string().trim().url('Enter a valid RSS Feed URL').required('RSS Feed URL is required'),
            max_items: Yup.number().transform((value, originalValue) => (originalValue === '' ? null : value)).nullable().integer('Must be a whole number').min(1, 'Must be at least 1').max(10, 'Must be 10 or fewer'),
          })),
        })
      ).min(1, 'At least one location is required'),
    }),
  }),
    onSubmit: async (values: any, actions: any) => {
      if (typeof File !== 'undefined' && values.logo instanceof File && values.logo.size > MAX_LOGO_SIZE_BYTES) {
        toaster({ message: 'Business logo exceeds 1 MB. Please choose a business logo no larger than 1 MB.', success: false });
        return;
      }

      if (typeof File !== 'undefined' && Array.isArray(values.locations)) {
        const oversizedLocationIndex = values.locations.findIndex(
          (location: any) => location.logoFile instanceof File && location.logoFile.size > MAX_LOGO_SIZE_BYTES
        );
        if (oversizedLocationIndex !== -1) {
          const locationName = values.locations[oversizedLocationIndex].display_name?.trim();
          const locationLabel = locationName || `location ${oversizedLocationIndex + 1}`;
          toaster({ message: `Logo for ${locationLabel} exceeds 1 MB. Please choose a location logo no larger than 1 MB.`, success: false });
          return;
        }
      }

      handelCompanyUpdate(values)
    }
  });

  useEffect(() => {
    if (formik.values.country_id) {
      fetchStates(formik.values.country_id)
    }
  }, [formik.values.country_id])

  const handelCompanyUpdate = async (values: any) => {
    try {
      const { firstName, lastName, organizationName, websiteURL, phoneNumber, logo, question_id, answer, address, zipcode, country_id, state_id, city, locations } = values;

      // 1. Process location logos (only executed when user clicks Save Changes)
      if (canManageLocations && Array.isArray(locations)) {
        for (const loc of locations) {
          const locId = loc.id || loc.location_id;
          if (!locId) continue;

          if (loc.removeLogo) {
            await deleteData(`/user/location/${locId}/logo`);
          } else if (loc.logoFile instanceof File) {
            const locFormData = new FormData();
            locFormData.append('logo', loc.logoFile);
            await postData(`/user/location/${locId}/logo`, locFormData);
          }
        }
      }

      // 2. Process organization info & logo
      const isLogoDeleted = isLogoRemoved && !previewImage;
      // NOTE: email intentionally excluded — not editable by any role
      let payload = {
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber,
        business_name: organizationName,
        website_url: websiteURL,
        address, zipcode, country_id, state_id, city,
        // Only send locations if this role is actually allowed to manage them —
        // avoids sending a stale/read-only locations array from non-admin roles.
        ...(canManageLocations && {
          locations: locations.map(({ logoFile, _tempId, ...rest }: any) => rest),
        }),
        previousLogo: companyDetail?.filename,
        removeLogo: isLogoDeleted,
        question_id: answer ? question_id : null,
        answer
      };
      const formData = new FormData();
      formData.append('body', JSON.stringify(payload));
      if (logo instanceof File) {
        formData.append('logo', logo);
      }

      const response = await postData(API_ENDPOINTS.UPDATE_USER_INFO, formData);
      const { message, success } = response;
      if (success) {
        const updatedUser = await fetchData(API_ENDPOINTS.GET_USER_INFO);
        if (updatedUser?.success) dispatch(setUserData(updatedUser.data));
        navigate('/org/settings/profile');
      }
      toaster({ message, success });
    } catch (error) {
      toaster({ message: 'something went wrong', success: false });
    }
  }

  const addLocationHandler = () => {
    const blankLocationObject = [{
      _tempId: `loc-temp-${Date.now()}-${Math.random()}`,
      display_name: '',
      country_id: '',
      state_id: '',
      city: '',
      address: '',
      zipcode: '',
      reply_to_email: '',
      from_name: '',
      is_active: true,
      location_timezone: 'UTC',
      rss_feeds: [],
    }]
    formik.setFieldValue('locations', [...blankLocationObject, ...formik.values.locations]);
  };

  const removeLocationHandler = (index: number) => {
    const updatedLocations = [...formik.values.locations];
    updatedLocations.splice(index, 1);
    formik.setFieldValue('locations', updatedLocations);
  };

  const handleImageChange = (selectedImage: any) => {
    if (selectedImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(selectedImage);
    }
  };

  return (
    <div>
      <Form onSubmit={formik.handleSubmit} noValidate>
        <div className="main-title ">
          <h2>
            <span className="d-lg-none cursor-pointer" onClick={()=>onShowSidebar()}><FontAwesomeIcon icon={faBars} /></span>
            <p><span onClick={() => navigate("/org/settings/profile")}><FontAwesomeIcon icon={faPlay} />Edit Profile</span></p>
          </h2>
        </div>
        <div className="add-contact">
          <Row>
            <Col xl={4} md={6} xs={12}>
              <Form.Group className="form-group">
                <Form.Label>
                  Email Address
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="profile-email-tooltip" className="text-start">You cannot change this email address because it is verified and cannot be changed.</Tooltip>}
                  >
                    <span className="ms-2 cursor-pointer text-muted">
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  value={formik.values.email}
                  disabled
                  readOnly
                  title="You cannot change this email address because it is verified and cannot be changed."
                />
              </Form.Group>
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XInput name='firstName' label='First Name' formik={formik} />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XInput name='lastName' label='Last Name' formik={formik} />
            </Col>
            {+role_id === ROLE.ORG && <>
              <Col xl={4} md={6} xs={12}>
                <XInput name='organizationName' label='Organization Name' formik={formik} />
              </Col>
              <Col xl={4} md={6} xs={12}>
                <Form.Group className={`form-group position-relative`}>
                  <Form.Label>Upload Business Logo</Form.Label>
                  <div className='position-relative'>
                    <Form.Control
                      ref={fileInputRef}
                      type='file'
                      name='logo'
                      id='business-logo-file'
                      accept='image/*'
                      className='visually-hidden'
                      onChange={(e) => {
                        const file = (e.currentTarget as HTMLInputElement).files?.[0];
                        if (file) {
                          if (file.size > MAX_LOGO_SIZE_BYTES) {
                            toaster({ message: 'Business logo exceeds 1 MB. Please choose a business logo no larger than 1 MB.', success: false });
                            e.currentTarget.value = '';
                            return;
                          }
                          handleImageChange(file);
                          formik.setFieldValue('logo', file);
                          setIsLogoRemoved(false);
                        }
                      }}
                    />
                    <label htmlFor='business-logo-file' className='logo-dropzone'>
                      <svg className='logo-dropzone-icon' viewBox='0 0 24 24' aria-hidden='true'>
                        <path d='M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14.5v3A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-3' />
                      </svg>
                      <span className='logo-dropzone-action'>{previewImage || (!isLogoRemoved && companyDetail?.logoUrl) ? 'Edit Logo' : 'Upload logo'}</span>
                      {(previewImage || (!isLogoRemoved && companyDetail?.logoUrl)) && (
                        <img className='logo-dropzone-preview' alt='Business logo preview' src={previewImage || companyDetail?.logoUrl} />
                      )}
                    </label>
                    {(previewImage || (!isLogoRemoved && companyDetail?.logoUrl)) && (
                      <button
                        type='button'
                        className='logo-dropzone-remove'
                        aria-label='Remove business logo'
                        title='Remove Logo'
                        onClick={() => {
                          setPreviewImage(null);
                          setIsLogoRemoved(true);
                          formik.setFieldValue('logo', null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        <FontAwesomeIcon icon={faCircleXmark} />
                      </button>
                    )}
                  </div>
                  {formik?.errors?.logo && formik?.touched?.logo && typeof formik.errors.logo === 'string' && (
                    <Form.Control.Feedback type="invalid">
                      {formik?.errors?.logo}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col xl={4} md={6} xs={12}>
                <XInput label='Website URL' name='websiteURL' formik={formik} />
              </Col>
            </>
            }

            <Col xl={4} md={6} xs={12}>
              <PhoneNumberInput
                value={formik.values.phoneNumber}
                onChange={phone => formik.setFieldValue('phoneNumber', phone)}
                error={formik.errors.phoneNumber}
                touched={formik.touched.phoneNumber}
              />
            </Col>

            {/* Personal address — editable by ANY role */}
            <Col xl={4} md={6} xs={12}>
              <XInput name='address' label='Address' formik={formik} />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XSelect
                name='country_id'
                label="Country"
                options={Object.values(countries)}
                formik={formik}
                onChange={(e: any) => {
                  formik.handleChange(e);
                  formik.setFieldValue('state_id', '');
                }}
              />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XSelect
                name='state_id'
                label="State"
                options={Object.values(states)}
                formik={formik}
              />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XInput name='city' label="City" formik={formik} />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XInput name='zipcode' label='ZIP Code' formik={formik} />
            </Col>
          </Row>

          {/* Org business locations — Org/Account Admin ONLY */}
          {canManageLocations && (
            <>
              <p className="text-end add-link">
                <span className='btn-add' onClick={addLocationHandler}>
                  <FontAwesomeIcon icon={faPlus} /> <span>Add Location</span>
                </span>
              </p>
              <div
                className="locations-scroll-container"
                style={formik.values.locations.length > 2 ? {
                  maxHeight: '680px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  paddingRight: '6px',
                  paddingBottom: '10px',
                  marginBottom: '15px'
                } : undefined}
              >
                {
                  formik.values.locations.map((v: any, index: number) => (
                    <LocationCard
                      key={v.id || v.location_id || v._tempId || index}
                      index={index}
                      locationData={v}
                      formik={formik}
                      removeLocationHandler={removeLocationHandler}
                    />
                  ))
                }
              </div>
            </>
          )}
        </div>
        <div className="btns">
          <div className="d-flex justify-content-between align-items-center">
            <XBackButton />
            <XButton
              type="submit"
              buttonText={<>Save Changes<FontAwesomeIcon icon={faPlay} /></>}
              loading={isLoading}
            />
          </div>
        </div>
      </Form>
    </div>
  );
};

export default EditCompany;
